import React, { useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Settings, Trash2, Eye, EyeOff, X, Cloud, Database, Type, FilePlus, ToggleLeft, ToggleRight, Mic, Plus, CreditCard, Smartphone, Layers } from 'lucide-react';
import api from '../api';
import AlertToast from './AlertToast';
import ConfirmModal from './ConfirmModal';
import CategoryManagement from './CategoryManagement';
import { isFontLoaded, isFontLoading, isFontFailed, prefetchAllFontsInIdle, ensureCustomFontsLoaded, loadSingleFont, refreshFontRegistry } from '../fontLoader';
import { API_BASE_URL } from '../constants';
import { IS_EDU, HAS_PAYMENT } from '../config/edition'; // 🎓 教育版隐藏支付配置

// ============================================================================
//  💳 支付默认配置（虎皮椒）
// ============================================================================
// ⚠️ 必须放在**模块顶层**，不能写在 useState(() => ...) 里面。
//    原因：useState 的初始值是惰性函数，terser 不会把箭头函数体内的
//    三元表达式折叠掉 → 教育版产物里会原样留着虎皮椒接口地址、
//    「请使用微信扫码支付」和整张价目表（1/3/5…500）。实测过。
//
//    提到顶层后，HAS_PAYMENT 是编译期字面量比较，terser 能把整个
//    常量初始化折叠成 undefined → 这些字符串**根本不进 bundle**。
//
//    判定只用 process.env.REACT_APP_SITE_EDITION（见 config/edition.js）：
//    edu / *-edu 一律无支付。
const PAYMENT_DEFAULT_CONFIG = HAS_PAYMENT ? {
  appid: '', appsecret: '', pay_channel: 'wechat',
  api_url: 'https://api.xunhupay.com/payment/do.html',
  qr_hint: '请使用微信扫码支付',
  rate: 10, packages: [{amount: 1, credits: null}, {amount: 3, credits: null}, {amount: 5, credits: null}, {amount: 10, credits: null}, {amount: 30, credits: null}, {amount: 50, credits: null}, {amount: 100, credits: null}, {amount: 300, credits: null}, {amount: 500, credits: null}],
  custom_enabled: true, custom_min: 1, custom_max: 500
} : {};

/**
 * ApiConfigModal — 系统配置弹窗（精简版）
 *
 * 仅保留：字体管理、语音识别、云存储（OSS/R2）、支付、短信配置。
 * AI 模型/API 配置已拆分到独立的 ApiProviderModal。
 */

// 样式
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 4000 };
const modalStyle = { width: 900, height: '85vh', background: 'var(--modal-surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, color: 'var(--text-body)', boxShadow: '0 20px 60px rgba(0,0,0,0.6)', display: 'flex', flexDirection: 'column' };
const header = { padding: '14px 16px', borderBottom: '1px solid var(--border-subtle)', fontWeight: 700, fontSize: 14, flexShrink: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const bodyStyle = { display: 'flex', flex: 1, overflow: 'hidden', minHeight: 0 };
const leftPanel = { width: 280, borderRight: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', overflow: 'hidden', userSelect: 'none' };
const rightPanel = { flex: 1, padding: 16, overflowY: 'auto' };
const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--border-subtle)', background: 'var(--modal-input-bg)', color: 'var(--text-primary)', outline: 'none', fontSize: 13, boxSizing: 'border-box' };
const inputStyleSmall = { ...inputStyle, fontSize: 11, fontFamily: 'monospace' };
const labelStyle = { fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4, display: 'block' };
const btn = (primary) => ({ padding: '8px 16px', borderRadius: 8, border: '1px solid ' + (primary ? '#3b82f6' : 'var(--border-subtle)'), background: primary ? '#3b82f6' : 'transparent', color: primary ? '#fff' : 'var(--text-body)', cursor: 'pointer', fontSize: 13 });
// 存储模式徽标配色（'local' 不显示徽标）
const STORAGE_BADGE = {
  oss: { label: 'OSS', bg: 'rgba(16,185,129,0.15)', fg: '#34d399' },
  r2: { label: 'R2', bg: 'rgba(249,115,22,0.15)', fg: '#fb923c' },
  minio: { label: 'MinIO', bg: 'rgba(139,92,246,0.15)', fg: '#a78bfa' },
};

// ============================================================================
//  字体管理面板（内部子组件）
// ============================================================================
function FontsPanel({ adminFonts, setAdminFonts, fontForm, setFontForm, fontUploading, setFontUploading, fontFileRef, showToast, labelStyle, inputStyle, btn }) {
  const API_BASE = API_BASE_URL;

  // 字体下载状态变化时刷新列表显示
  const [, _forceUpdateFontUI] = React.useState(0);
  React.useEffect(() => {
    const handler = () => _forceUpdateFontUI(n => n + 1);
    window.addEventListener('fontsStateChanged', handler);
    ensureCustomFontsLoaded();
    prefetchAllFontsInIdle();
    return () => window.removeEventListener('fontsStateChanged', handler);
  }, []);
  React.useEffect(() => {
    const missingFamilies = adminFonts
      .filter(font => font.enabled && !isFontLoaded(font.family) && !isFontLoading(font.family) && !isFontFailed(font.family))
      .map(font => font.family);

    if (missingFamilies.length === 0) return;

    let cancelled = false;
    (async () => {
      for (const family of missingFamilies) {
        if (cancelled) return;
        await loadSingleFont(family).catch(() => {});
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [adminFonts]);
  const [detecting, setDetecting] = React.useState(false);
  const [, setFamilyAutoDetected] = React.useState(false);
  const [fontFileName, setFontFileName] = React.useState('');
  // 上传阶段：null | 'uploading' | 'converting' | 'loading'
  const [fontUploadPhase, setFontUploadPhase] = React.useState(null);

  // 🛡️ FontsPanel 直接复用父组件传入的 showToast（包含定时器清理逻辑）
  // showToast 已经通过 props 传入，无需重复定义

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFontFileName(file.name);

    const guessLabel = file.name
      .replace(/\.(ttf|otf|woff2?)/i, '')
      .replace(/[-_](Regular|Bold|Italic|Light|Medium|Black|Thin|ExtraBold|SemiBold)/gi, '')
      .replace(/[-_]/g, ' ')
      .trim();
    if (!fontForm.label) {
      setFontForm(f => ({ ...f, label: guessLabel }));
    }

    setDetecting(true);
    setFamilyAutoDetected(false);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.authFetch(`${API_BASE}/api/admin/fonts/detect`, {
        method: 'POST',
        body: fd,
      });
      const data = await res.json();
      if (data.family) {
        setFontForm(f => ({ ...f, family: data.family }));
        setFamilyAutoDetected(true);
      }
    } catch (_) {
    } finally {
      setDetecting(false);
    }
  };

  const handleUpload = async () => {
    const file = fontFileRef.current?.files?.[0];
    if (!file) return showToast('请先选择字体文件', 'warning');
    if (!fontForm.label.trim()) return showToast('请填写显示名称', 'warning');

    setFontUploading(true);
    setFontUploadPhase('uploading');
    try {
      const fd = new FormData();
      fd.append('category', fontForm.category);
      fd.append('label', fontForm.label.trim());
      fd.append('family', fontForm.family.trim());
      fd.append('file', file);

      // 用 XHR 代替 fetch，利用 upload.onload 捕捉"文件已传完、服务器处理中"的时机
      const data = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.onload = () => {
          // 文件字节已全部送达，等服务器响应
        };
        xhr.onload = () => {
          const raw = xhr.responseText || '';
          const contentType = (xhr.getResponseHeader('content-type') || '').toLowerCase();

          let result = null;
          if (contentType.includes('application/json')) {
            try {
              result = JSON.parse(raw);
            } catch (_) {
              // JSON 解析失败时走下方兜底错误
            }
          }

          if (xhr.status >= 200 && xhr.status < 300) {
            if (result && typeof result === 'object') {
              resolve(result);
              return;
            }
            reject(new Error(`服务器响应异常 (HTTP ${xhr.status})`));
            return;
          }

          const snippet = String(raw).replace(/\s+/g, ' ').trim().slice(0, 120);
          const msg = (result && (result.error || result.message))
            || (snippet ? `HTTP ${xhr.status}: ${snippet}` : `HTTP ${xhr.status}`)
            || '上传失败';
          reject(new Error(msg));
        };
        xhr.onerror = () => reject(new Error('网络连接失败'));
        xhr.open('POST', `${API_BASE}/api/admin/fonts`);
        const token = api.getToken();
        if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        xhr.send(fd);
      });

      refreshFontRegistry();
      setAdminFonts(prev => [...prev, data.font]);

      let activated = isFontLoaded(data.font.family);
      if (!activated) {
        setFontUploadPhase('loading');
        await ensureCustomFontsLoaded([data.font.family]);
        activated = await loadSingleFont(data.font.family);
      }

      setFontForm({ label: '', family: '', category: 'chinese' });
      setFamilyAutoDetected(false);
      setFontFileName('');
      if (fontFileRef.current) fontFileRef.current.value = '';
      window.dispatchEvent(new Event('fontsUpdated'));
      const fmt = (data.font.format || '').toUpperCase();
      const toastMsg = `✅ 字体 "${data.font.label}" 已添加（${fmt} 格式），font-family: "${data.font.family}"`;
      if (activated) {
        showToast(toastMsg);
      } else {
        showToast(`⚠️ 字体 "${data.font.label}" 已上传，但当前浏览器未能激活预览，可删除后重传`, 'warning');
      }
    } catch (err) {
      showToast('❌ ' + err.message, 'warning');
    } finally {
      setFontUploadPhase(null);
      setFontUploading(false);
    }
  };

  const handleToggle = async (font) => {
    try {
      const res = await api.authFetch(`${API_BASE}/api/admin/fonts/${font.id}/toggle`, {
        method: 'PATCH',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '操作失败');
      setAdminFonts(prev => prev.map(f => f.id === font.id ? { ...f, enabled: data.enabled } : f));
      window.dispatchEvent(new Event('fontsUpdated'));
    } catch (err) {
      showToast('❌ ' + err.message, 'warning');
    }
  };

  const handleDelete = async (font, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(`确认删除字体 “${font.label}” 吗？此操作会同时删除服务器文件。`)) return;
    try {
      const res = await api.authFetch(`${API_BASE}/api/admin/fonts/${font.id}`, {
        method: 'DELETE',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || '删除失败');
      setAdminFonts(prev => prev.filter(f => f.id !== font.id));
      showToast(`🗑️ 字体 "${font.label}" 已删除`);
      window.dispatchEvent(new Event('fontsUpdated'));
    } catch (err) {
      showToast('❌ ' + err.message, 'warning');
    }
  };

  const chineseFonts = adminFonts.filter(f => f.category === 'chinese');
  const englishFonts = adminFonts.filter(f => f.category === 'english');

  const ROW_H = 30;
  const formatLabel = (format) => {
    const normalized = String(format || '').toLowerCase();
    if (normalized === 'truetype') return 'TTF';
    if (normalized === 'opentype') return 'OTF';
    if (normalized === 'woff') return 'WOFF';
    if (normalized === 'woff2') return 'WOFF2';
    return (format || 'UNKNOWN').toString().toUpperCase();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ paddingBottom: 10, borderBottom: '1px solid var(--border-subtle)' }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--text-body)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <Type size={16} />
          字体管理
        </h3>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
          上传自定义字体，所有用户在文字工具中均可使用。支持 TTF / OTF / WOFF / WOFF2，最大 50 MB。
        </p>
      </div>

      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          type="file"
          ref={fontFileRef}
          accept=".ttf,.otf,.woff,.woff2"
          onChange={handleFileChange}
          style={{ display: 'none' }}
          id="font-file-input"
        />
        <label
          htmlFor="font-file-input"
          title="选择字体文件（TTF / OTF / WOFF / WOFF2）"
          style={{
            flex: '0 0 auto', width: 120, height: ROW_H, boxSizing: 'border-box',
            padding: '0 8px', borderRadius: 6, border: '1px solid var(--border-subtle)',
            background: 'var(--modal-input-bg)', color: fontFileName ? 'var(--text-body)' : 'var(--text-muted)',
            fontSize: 12, cursor: 'pointer', whiteSpace: 'nowrap',
            overflow: 'hidden', textOverflow: 'ellipsis', display: 'flex',
            alignItems: 'center'
          }}
        >
          {fontFileName || '选择文件…'}
        </label>
        <input
          style={{ ...inputStyle, flex: 1.2, minWidth: 60, height: ROW_H, padding: '0 10px', boxSizing: 'border-box' }}
          placeholder="显示名称"
          value={fontForm.label}
          onChange={e => setFontForm(f => ({ ...f, label: e.target.value }))}
        />
        <input
          style={{ ...inputStyle, flex: 1.2, minWidth: 60, height: ROW_H, padding: '0 10px', boxSizing: 'border-box', opacity: detecting ? 0.6 : 1 }}
          placeholder={detecting ? '识别中…' : 'Family 名'}
          value={fontForm.family}
          disabled={detecting}
          onChange={e => { setFontForm(f => ({ ...f, family: e.target.value })); setFamilyAutoDetected(false); }}
        />
        {[{ value: 'chinese', label: '中' }, { value: 'english', label: '英' }].map(opt => (
          <button
            key={opt.value}
            onClick={() => setFontForm(f => ({ ...f, category: opt.value }))}
            style={{
              flexShrink: 0, height: ROW_H, padding: '0 10px', boxSizing: 'border-box',
              borderRadius: 6, fontSize: 12,
              border: `1px solid ${fontForm.category === opt.value ? '#3b82f6' : 'var(--border-subtle)'}`,
              background: fontForm.category === opt.value ? 'rgba(59,130,246,0.25)' : 'var(--modal-input-bg)',
              color: fontForm.category === opt.value ? '#93c5fd' : 'var(--text-secondary)',
              cursor: 'pointer'
            }}
          >
            {opt.label}
          </button>
        ))}
        <button
          onClick={handleUpload}
          disabled={fontUploading}
          title={fontUploadPhase === 'loading' ? '载入中...' : fontUploading ? '上传中...' : '上传并注册字体'}
          style={{
            flexShrink: 0, height: ROW_H, padding: '0 12px', boxSizing: 'border-box',
            borderRadius: 6, border: 'none',
            background: fontUploadPhase === 'loading' ? 'rgba(245,158,11,0.4)' : fontUploading ? 'rgba(59,130,246,0.3)' : '#3b82f6',
            color: '#fff', cursor: fontUploading ? 'not-allowed' : 'pointer',
            display: 'flex', alignItems: 'center', gap: 5, fontSize: 12,
            opacity: fontUploading ? 0.6 : 1, whiteSpace: 'nowrap'
          }}
        >
          {fontUploading
            ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
            : <FilePlus size={14} />}
          {fontUploadPhase === 'loading' ? '载入中...' : fontUploading ? '上传中...' : '注册'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, position: 'relative' }}>
        {fontUploading && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 10,
            background: 'rgba(15,23,42,0.75)', borderRadius: 8,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10,
          }}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
              stroke={fontUploadPhase === 'loading' ? '#f59e0b' : '#4fc3f7'}
              strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}>
              <path d="M21 12a9 9 0 1 1-6.219-8.56"/>
            </svg>
            <span style={{ fontSize: 13, color: fontUploadPhase === 'loading' ? '#fcd34d' : '#93c5fd', fontWeight: 600 }}>
              {fontUploadPhase === 'loading' ? '载入中，正在激活字体预览...' : '上传中，请稍候...'}
            </span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {fontUploadPhase === 'loading' ? '字体可预览后将自动完成' : '上传完成前请勿关闭'}
            </span>
          </div>
        )}
        {[{ key: 'chinese', label: '中文字体', fonts: chineseFonts }, { key: 'english', label: '英文字体', fonts: englishFonts }].map(group => (
          <div key={group.key}>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 5, letterSpacing: 0.5 }}>
              {group.label}（{group.fonts.length}）
            </div>
            {group.fonts.length === 0 ? (
              <div style={{ fontSize: 11, color: '#334155', padding: '8px 0', textAlign: 'center', border: '1px dashed var(--border-subtle)', borderRadius: 6 }}>暂无</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {group.fonts.map(font => (
                  <div
                    key={font.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '6px 8px',
                      background: font.enabled ? 'var(--modal-input-bg)' : 'var(--modal-input-bg)',
                      border: `1px solid ${font.enabled ? 'rgba(59,130,246,0.2)' : 'var(--modal-input-border)'}`,
                      borderRadius: 6, opacity: font.enabled ? 1 : 0.5
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, lineHeight: 1.4, color: 'var(--text-body)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: `"${font.family}", sans-serif` }}>{font.label}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 10, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, flex: 1 }}>{font.family}</div>
                        <span
                          title={`字体文件格式: ${formatLabel(font.format)}`}
                          style={{
                            flexShrink: 0,
                            fontSize: 9,
                            lineHeight: 1,
                            padding: '2px 5px',
                            borderRadius: 999,
                            border: '1px solid rgba(148,163,184,0.45)',
                            color: '#94a3b8',
                            background: 'rgba(15,23,42,0.55)',
                            letterSpacing: 0.3
                          }}
                        >
                          {formatLabel(font.format)}
                        </span>
                      </div>
                    </div>
                    {isFontLoading(font.family)
                      ? <span style={{ fontSize: 10, color: '#4fc3f7', display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>加载
                        </span>
                      : isFontFailed(font.family)
                        ? <span style={{ minWidth: 44, justifyContent: 'flex-end', fontSize: 10, color: '#ef4444', display: 'flex', alignItems: 'center', flexShrink: 0 }} title="字体加载失败，建议删除后重传">失败</span>
                        : !isFontLoaded(font.family) && <span style={{ minWidth: 52, justifyContent: 'flex-end', fontSize: 10, color: '#f59e0b', display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }} title="字体正在载入浏览器预览">
                          <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ animation: 'spin 1.2s linear infinite' }}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>载入中
                        </span>
                    }
                    <button onClick={() => handleToggle(font)} title={font.enabled ? '禁用' : '启用'} style={{ background: 'none', border: 'none', cursor: 'pointer', color: font.enabled ? '#3b82f6' : '#475569', display: 'flex', alignItems: 'center', padding: 4, flexShrink: 0, borderRadius: 4 }}>
                      {font.enabled ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                    </button>
                    <button onClick={(e) => handleDelete(font, e)} title="删除" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', display: 'flex', alignItems: 'center', padding: 4, flexShrink: 0, borderRadius: 4, transition: 'all 0.15s' }} onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; }} onMouseLeave={e => { e.currentTarget.style.color = '#475569'; }}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ padding: 12, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
        <div style={{ fontWeight: 600, color: '#93c5fd', marginBottom: 4 }}>💡 使用说明</div>
        <ul style={{ margin: 0, paddingLeft: 16, lineHeight: 1.7 }}>
          <li>上传后字体立即对所有用户生效，无需重启服务</li>
          <li>支持 TTF / OTF / WOFF / WOFF2，现代浏览器均可直接使用</li>
          <li>若追求更小体积，建议上传前在本地将 TTF/OTF 转为 WOFF2（可用 fonttools 等工具）</li>
          <li>若未填写 Family 名，系统会按文件名自动生成，可随时手动修改</li>
          <li>禁用：用户看不到，但文件保留；删除：文件会从磁盘永久移除</li>
        </ul>
      </div>
    </div>
  );
}

export default function ApiConfigModal({ open, onClose }) {
  const modalRef = useRef(null);

  // 状态
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // 选中状态: null | { type: 'fonts' } | { type: 'voice' } | { type: 'oss' } | { type: 'r2' } | { type: 'minio' } | { type: 'payment' } | { type: 'sms' }
  const [activeSelection, setActiveSelection] = useState(null);

  // OSS 配置状态
  const [ossConfig, setOssConfig] = useState({ region: '', mediaBucket: '', refBucket: '', accessKeyId: '', accessKeySecret: '', endpoint: '' });
  const [ossConfigured, setOssConfigured] = useState(false);
  const [ossTesting, setOssTesting] = useState(false);
  const [showOssKeyId, setShowOssKeyId] = useState(false);
  const [showOssKeySecret, setShowOssKeySecret] = useState(false);

  // R2 配置状态
  const [r2Config, setR2Config] = useState({ accountId: '', bucket: '', refBucket: '', accessKeyId: '', secretAccessKey: '', publicDomain: '', refPublicDomain: '' });
  const [r2Configured, setR2Configured] = useState(false);
  const [r2Testing, setR2Testing] = useState(false);
  const [showR2KeyId, setShowR2KeyId] = useState(false);
  const [showR2KeySecret, setShowR2KeySecret] = useState(false);

  // MinIO 配置状态（自建对象存储，字段形状刻意与 R2 一致，只多一个 endpoint）
  const [minioConfig, setMinioConfig] = useState({ endpoint: '', bucket: '', refBucket: '', accessKeyId: '', secretAccessKey: '', publicDomain: '', refPublicDomain: '' });
  const [minioConfigured, setMinioConfigured] = useState(false);
  const [minioTesting, setMinioTesting] = useState(false);
  const [showMinioKeyId, setShowMinioKeyId] = useState(false);
  const [showMinioKeySecret, setShowMinioKeySecret] = useState(false);

  // 存储模式: 'oss' | 'r2' | 'minio' | 'local'
  const [storageMode, setStorageMode] = useState('local');

  // 语音识别 (ASR) 配置状态
  const [, setAsrConfig] = useState({ appId: '', accessToken: '' });
  const [asrConfigured, setAsrConfigured] = useState(false);
  const [asrTesting, setAsrTesting] = useState(false);
  const [asrSaving, setAsrSaving] = useState(false);
  const [asrInstances, setAsrInstances] = useState([]);
  const [selectedAsrIdx, setSelectedAsrIdx] = useState(0);
  const [showAsrToken, setShowAsrToken] = useState(false);

  // 字体管理状态
  const [adminFonts, setAdminFonts] = useState([]);
  const [fontUploading, setFontUploading] = useState(false);
  const [fontForm, setFontForm] = useState({ label: '', family: '', category: 'chinese' });
  const fontFileRef = useRef(null);

  // 支付配置状态（默认值见模块顶层 PAYMENT_DEFAULT_CONFIG）
  const [paymentConfig, setPaymentConfig] = useState(() => PAYMENT_DEFAULT_CONFIG);
  const [paymentConfigured, setPaymentConfigured] = useState(false);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [showPaymentSecret, setShowPaymentSecret] = useState(false);
  const [newPackageAmount, setNewPackageAmount] = useState('');

  // 短信配置状态
  const [smsConfig, setSmsConfig] = useState({ accessKeyId: '', accessKeySecret: '', signName: '', templateCodeRegister: '', templateCodeReset: '' });
  const [smsConfigured, setSmsConfigured] = useState(false);
  const [smsSaving, setSmsSaving] = useState(false);
  const [showSmsSecret, setShowSmsSecret] = useState(false);

  // 🆕 资产库配置状态
  const [showCategoryManagement, setShowCategoryManagement] = useState(false);

  // Toast 和确认弹窗状态
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('success');
  const [confirmModal] = useState(null);

  // 🛡️ Toast 定时器清理：防止组件卸载后 setState 泄漏
  const toastTimerRef = useRef(null);
  const showToast = (msg, type = 'success', duration = 5000) => {
    clearTimeout(toastTimerRef.current);
    setToastMessage(msg);
    setToastType(type);
    toastTimerRef.current = setTimeout(() => setToastMessage(''), duration);
  };
  useEffect(() => () => clearTimeout(toastTimerRef.current), []);

  // 加载数据（仅系统配置 API）
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    Promise.all([
      api.get('/api/settings/oss'),
      api.get('/api/admin/fonts'),
      api.get('/api/settings/voice-asr').catch(() => ({ configured: false })),
      api.get('/api/settings/voice-asr/instances').catch(() => ({ instances: [] })),
      api.get('/api/settings/r2').catch(() => ({ configured: false })),
      api.get('/api/settings/minio').catch(() => ({ configured: false })),
      api.get('/api/settings/storage-mode').catch(() => ({ mode: 'local' })),
      // 🎓 教育版后端没挂支付路由，这里连请求都别发（顺带让路径字符串不进 bundle）
      IS_EDU ? Promise.resolve({ configured: false }) : api.get('/api/payment/config').catch(() => ({ configured: false })),
      api.get('/api/sms/config').catch(() => ({ configured: false })),
    ]).then(([ossData, fontsData, asrData, asrInstancesData, r2Data, minioData, storageModeData, paymentData, smsData]) => {
      setAdminFonts(Array.isArray(fontsData) ? fontsData : []);
      setOssConfigured(ossData.configured || false);
      if (ossData.configured) {
        setOssConfig({
          region: ossData.region || '',
          mediaBucket: ossData.mediaBucket || ossData.bucket || '',
          refBucket: ossData.refBucket || ossData.mediaBucket || ossData.bucket || '',
          accessKeyId: ossData.accessKeyId || '',
          accessKeySecret: ossData.accessKeySecret || '',
          endpoint: ossData.endpoint || ''
        });
      }
      setR2Configured(r2Data.configured || false);
      if (r2Data.configured) {
        setR2Config({
          accountId: r2Data.accountId || '',
          bucket: r2Data.bucket || '',
          refBucket: r2Data.refBucket || '',
          accessKeyId: r2Data.accessKeyId || '',
          secretAccessKey: r2Data.secretAccessKey || '',
          publicDomain: r2Data.publicDomain || '',
          refPublicDomain: r2Data.refPublicDomain || '',
        });
      }
      setMinioConfigured(minioData.configured || false);
      if (minioData.configured) {
        setMinioConfig({
          endpoint: minioData.endpoint || '',
          bucket: minioData.bucket || '',
          refBucket: minioData.refBucket || '',
          accessKeyId: minioData.accessKeyId || '',
          secretAccessKey: minioData.secretAccessKey || '',
          publicDomain: minioData.publicDomain || '',
          refPublicDomain: minioData.refPublicDomain || '',
        });
      }
      setStorageMode(storageModeData.mode || 'local');
      setAsrConfigured(asrData.configured || false);
      if (asrData.configured) {
        setAsrConfig({
          appId: asrData.appId || '',
          accessToken: asrData.accessToken || '',
        });
      }
      setAsrInstances(asrInstancesData?.instances || []);
      setPaymentConfigured(paymentData.configured || false);
      if (paymentData.configured && paymentData.config) {
        const cfg = { ...paymentData.config };
        if (Array.isArray(cfg.packages)) {
          cfg.packages = cfg.packages.map(p =>
            typeof p === 'object' && p !== null ? p : { amount: Number(p), credits: null }
          );
        }
        setPaymentConfig(prev => ({ ...prev, ...cfg }));
      }
      setSmsConfigured(smsData.configured || false);
      if (smsData.configured && smsData.config) {
        setSmsConfig(prev => ({ ...prev, ...smsData.config }));
      }
      setActiveSelection(null);
    }).catch(err => {
      console.error('加载配置失败', err);
    }).finally(() => {
      setLoading(false);
    });
  }, [open]);

  // OSS 配置相关函数
  const handleOssConfigChange = (field, value) => {
    setOssConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleOssTest = async () => {
    if (!ossConfig.region || !ossConfig.mediaBucket || !ossConfig.accessKeyId || !ossConfig.accessKeySecret) {
      showToast('⚠️ 请填写完整的 OSS 配置信息', 'warning');
      return;
    }
    setOssTesting(true);
    try {
      const data = await api.post('/api/settings/oss/test', ossConfig);
      if (data.success) {
        showToast('✅ ' + data.message, 'success');
      } else {
        showToast('❌ ' + data.error, 'warning');
      }
    } catch (err) {
      showToast('❌ 测试失败: ' + err.message, 'warning');
    } finally {
      setOssTesting(false);
    }
  };

  const handleOssSave = async () => {
    if (!ossConfig.region || !ossConfig.mediaBucket || !ossConfig.accessKeyId || !ossConfig.accessKeySecret) {
      showToast('⚠️ 请填写完整的 OSS 配置信息', 'warning');
      return;
    }
    setSaving(true);
    try {
      const data = await api.post('/api/settings/oss', ossConfig);
      if (data.success) {
        showToast('✅ OSS 配置已保存', 'success');
        setOssConfigured(true);
      } else {
        showToast('❌ ' + data.error, 'warning');
      }
    } catch (err) {
      showToast('❌ 保存失败: ' + err.message, 'warning');
    } finally {
      setSaving(false);
    }
  };

  // R2 配置相关函数
  const handleR2ConfigChange = (field, value) => {
    setR2Config(prev => ({ ...prev, [field]: value }));
  };

  const handleR2Test = async () => {
    if (!r2Config.accountId || !r2Config.bucket || !r2Config.accessKeyId || !r2Config.secretAccessKey) {
      showToast('⚠️ 请填写完整的 R2 配置（账户 ID、素材桶、访问密钥 ID、机密访问密钥）', 'warning');
      return;
    }
    setR2Testing(true);
    try {
      const data = await api.post('/api/settings/r2/test', r2Config);
      if (data.success) {
        showToast('✅ ' + data.message, 'success');
      } else {
        showToast('❌ ' + data.error, 'warning');
      }
    } catch (err) {
      showToast('❌ 测试失败: ' + err.message, 'warning');
    } finally {
      setR2Testing(false);
    }
  };

  const handleR2Save = async () => {
    if (!r2Config.accountId || !r2Config.bucket || !r2Config.accessKeyId || !r2Config.secretAccessKey) {
      showToast('⚠️ 请填写完整的 R2 配置（账户 ID、素材桶、访问密钥 ID、机密访问密钥）', 'warning');
      return;
    }
    setSaving(true);
    try {
      const data = await api.post('/api/settings/r2', r2Config);
      if (data.success) {
        showToast('✅ R2 配置已保存', 'success');
        setR2Configured(true);
      } else {
        showToast('❌ ' + data.error, 'warning');
      }
    } catch (err) {
      showToast('❌ 保存失败: ' + err.message, 'warning');
    } finally {
      setSaving(false);
    }
  };

  // MinIO 配置相关函数（自建对象存储 / 教育版）
  const handleMinioConfigChange = (field, value) => {
    setMinioConfig(prev => ({ ...prev, [field]: value }));
  };

  const minioIncomplete = () => !minioConfig.endpoint || !minioConfig.bucket || !minioConfig.accessKeyId || !minioConfig.secretAccessKey;

  const handleMinioTest = async () => {
    if (minioIncomplete()) {
      showToast('⚠️ 请填写完整的 MinIO 配置（服务地址、素材桶、访问密钥 ID、机密访问密钥）', 'warning');
      return;
    }
    setMinioTesting(true);
    try {
      const data = await api.post('/api/settings/minio/test', minioConfig);
      if (data.success) {
        showToast('✅ ' + data.message, 'success');
      } else {
        showToast('❌ ' + data.error, 'warning');
      }
    } catch (err) {
      showToast('❌ 测试失败: ' + err.message, 'warning');
    } finally {
      setMinioTesting(false);
    }
  };

  const handleMinioSave = async () => {
    if (minioIncomplete()) {
      showToast('⚠️ 请填写完整的 MinIO 配置（服务地址、素材桶、访问密钥 ID、机密访问密钥）', 'warning');
      return;
    }
    setSaving(true);
    try {
      const data = await api.post('/api/settings/minio', minioConfig);
      if (data.success) {
        showToast('✅ MinIO 配置已保存', 'success');
        setMinioConfigured(true);
      } else {
        showToast('❌ ' + data.error, 'warning');
      }
    } catch (err) {
      showToast('❌ 保存失败: ' + err.message, 'warning');
    } finally {
      setSaving(false);
    }
  };

  // 切换存储模式
  const handleSwitchStorageMode = async (mode) => {
    try {
      const data = await api.post('/api/settings/storage-mode', { mode });
      if (data.success) {
        setStorageMode(mode);
        showToast(`✅ ${data.message}`, 'success');
      } else {
        showToast('❌ ' + (data.error || '切换失败'), 'warning');
      }
    } catch (err) {
      showToast('❌ 切换失败: ' + err.message, 'warning');
    }
  };

  if (!open) return null;

  const modalContent = (
    <div style={{...overlay, pointerEvents: 'auto'}}>
      <div ref={modalRef} style={modalStyle}>

        {/* Header */}
        <div style={header}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Settings size={14} /> 系统配置</span>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
              padding: 4,
              display: 'flex',
              alignItems: 'center',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.color = 'var(--text-hover)'}
            onMouseLeave={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
          >
            <X size={18} />
          </button>
        </div>

        {/* Body: 左右分栏 */}
        <div style={bodyStyle}>
          {loading ? (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
              加载中...
            </div>
          ) : (
            <>
              {/* 左侧面板 — 系统配置项目列表 */}
              <div style={leftPanel}>
                {/* 上部：功能配置按钮列表 */}
                <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 6, flexShrink: 0 }}>
                  {/* 字体管理 */}
                  <button
                    onClick={() => setActiveSelection({ type: 'fonts' })}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      background: activeSelection?.type === 'fonts' ? 'rgba(59,130,246,0.2)' : 'var(--modal-input-bg)',
                      border: `1px solid ${activeSelection?.type === 'fonts' ? '#3b82f6' : 'var(--border-subtle)'}`,
                      borderRadius: 6,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12,
                      color: activeSelection?.type === 'fonts' ? '#93c5fd' : 'var(--text-secondary)',
                      fontWeight: activeSelection?.type === 'fonts' ? 600 : 400,
                      textAlign: 'left'
                    }}
                  >
                    <Type size={14} />
                    <span>字体管理</span>
                    {adminFonts.filter(f => f.enabled).length > 0 && (
                      <span style={{ marginLeft: 'auto', fontSize: 10, color: '#3b82f6' }}>
                        {adminFonts.filter(f => f.enabled).length} 个启用
                      </span>
                    )}
                  </button>

                  {/* 语音识别 */}
                  <button
                    onClick={() => setActiveSelection({ type: 'voice' })}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      background: activeSelection?.type === 'voice' ? 'rgba(168,85,247,0.2)' : 'var(--modal-input-bg)',
                      border: `1px solid ${activeSelection?.type === 'voice' ? '#a855f7' : 'var(--border-subtle)'}`,
                      borderRadius: 6,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12,
                      color: activeSelection?.type === 'voice' ? '#c084fc' : 'var(--text-secondary)',
                      fontWeight: activeSelection?.type === 'voice' ? 600 : 400,
                      textAlign: 'left'
                    }}
                  >
                    <Mic size={14} />
                    <span>语音识别配置</span>
                    {asrConfigured && <span style={{ marginLeft: 'auto', fontSize: 10, color: '#a855f7' }}>✓</span>}
                  </button>

                  {/* 充值支付（教育版无支付，整块不进 bundle） */}
                  {!IS_EDU && (
                  <button
                    onClick={() => setActiveSelection({ type: 'payment' })}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      background: activeSelection?.type === 'payment' ? 'rgba(234,179,8,0.15)' : 'var(--modal-input-bg)',
                      border: `1px solid ${activeSelection?.type === 'payment' ? '#eab308' : 'var(--border-subtle)'}`,
                      borderRadius: 6,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12,
                      color: activeSelection?.type === 'payment' ? '#facc15' : 'var(--text-secondary)',
                      fontWeight: activeSelection?.type === 'payment' ? 600 : 400,
                    }}
                  >
                    <CreditCard size={14} />
                    <span>充值支付配置</span>
                    {paymentConfigured && <span style={{ marginLeft: 'auto', fontSize: 10, color: '#eab308' }}>✓</span>}
                  </button>
                  )}

                  {/* 短信验证 */}
                  <button
                    onClick={() => setActiveSelection({ type: 'sms' })}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      background: activeSelection?.type === 'sms' ? 'rgba(16,185,129,0.15)' : 'var(--modal-input-bg)',
                      border: `1px solid ${activeSelection?.type === 'sms' ? '#10b981' : 'var(--border-subtle)'}`,
                      borderRadius: 6,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12,
                      color: activeSelection?.type === 'sms' ? '#34d399' : 'var(--text-secondary)',
                      fontWeight: activeSelection?.type === 'sms' ? 600 : 400,
                    }}
                  >
                    <Smartphone size={14} />
                    <span>短信验证配置</span>
                    {smsConfigured && <span style={{ marginLeft: 'auto', fontSize: 10, color: '#10b981' }}>✓</span>}
                  </button>

                  {/* 🆕 资产库配置 */}
                  <button
                    onClick={() => setActiveSelection({ type: 'categories' })}
                    style={{
                      width: '100%',
                      padding: '8px 10px',
                      background: activeSelection?.type === 'categories' ? 'rgba(99,102,241,0.15)' : 'var(--modal-input-bg)',
                      border: `1px solid ${activeSelection?.type === 'categories' ? '#6366f1' : 'var(--border-subtle)'}`,
                      borderRadius: 6,
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontSize: 12,
                      color: activeSelection?.type === 'categories' ? '#818cf8' : 'var(--text-secondary)',
                      fontWeight: activeSelection?.type === 'categories' ? 600 : 400,
                    }}
                  >
                    <Layers size={14} />
                    <span>资产库配置</span>
                  </button>
                </div>

                {/* 底部：云存储配置（推到最底下） */}
                <div style={{ marginTop: 'auto', padding: 12, borderTop: '1px solid var(--border-subtle)', flexShrink: 0 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 6, paddingLeft: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Cloud size={12} />
                    <span>云存储配置</span>
                    {storageMode !== 'local' && (
                      <span style={{ marginLeft: 'auto', fontSize: 10, padding: '1px 6px', borderRadius: 4, background: STORAGE_BADGE[storageMode]?.bg || 'rgba(249,115,22,0.15)', color: STORAGE_BADGE[storageMode]?.fg || '#fb923c' }}>
                        {STORAGE_BADGE[storageMode]?.label || storageMode.toUpperCase()} 启用中
                      </span>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => setActiveSelection({ type: 'oss' })}
                      style={{
                        flex: 1,
                        padding: '7px 8px',
                        background: activeSelection?.type === 'oss' ? 'rgba(16,185,129,0.2)' : 'var(--modal-input-bg)',
                        border: `1px solid ${activeSelection?.type === 'oss' ? '#10b981' : 'var(--border-subtle)'}`,
                        borderRadius: 6,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 4,
                        fontSize: 11,
                        color: activeSelection?.type === 'oss' ? '#34d399' : 'var(--text-secondary)',
                        fontWeight: activeSelection?.type === 'oss' ? 600 : 400,
                        position: 'relative'
                      }}
                    >
                      <Database size={12} />
                      <span>OSS</span>
                      {ossConfigured && <span style={{ fontSize: 9, color: '#10b981' }}>✓</span>}
                      {storageMode === 'oss' && <span style={{ position: 'absolute', top: -3, right: -3, width: 7, height: 7, borderRadius: '50%', background: '#10b981', border: '1.5px solid var(--modal-surface)' }} />}
                    </button>
                    <button
                      onClick={() => setActiveSelection({ type: 'r2' })}
                      style={{
                        flex: 1,
                        padding: '7px 8px',
                        background: activeSelection?.type === 'r2' ? 'rgba(249,115,22,0.2)' : 'var(--modal-input-bg)',
                        border: `1px solid ${activeSelection?.type === 'r2' ? '#f97316' : 'var(--border-subtle)'}`,
                        borderRadius: 6,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 4,
                        fontSize: 11,
                        color: activeSelection?.type === 'r2' ? '#fb923c' : 'var(--text-secondary)',
                        fontWeight: activeSelection?.type === 'r2' ? 600 : 400,
                        position: 'relative'
                      }}
                    >
                      <Cloud size={12} />
                      <span>R2</span>
                      {r2Configured && <span style={{ fontSize: 9, color: '#f97316' }}>✓</span>}
                      {storageMode === 'r2' && <span style={{ position: 'absolute', top: -3, right: -3, width: 7, height: 7, borderRadius: '50%', background: '#f97316', border: '1.5px solid var(--modal-surface)' }} />}
                    </button>
                    <button
                      onClick={() => setActiveSelection({ type: 'minio' })}
                      style={{
                        flex: 1,
                        padding: '7px 8px',
                        background: activeSelection?.type === 'minio' ? 'rgba(139,92,246,0.2)' : 'var(--modal-input-bg)',
                        border: `1px solid ${activeSelection?.type === 'minio' ? '#8b5cf6' : 'var(--border-subtle)'}`,
                        borderRadius: 6,
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 4,
                        fontSize: 11,
                        color: activeSelection?.type === 'minio' ? '#a78bfa' : 'var(--text-secondary)',
                        fontWeight: activeSelection?.type === 'minio' ? 600 : 400,
                        position: 'relative'
                      }}
                    >
                      <Database size={12} />
                      <span>MinIO</span>
                      {minioConfigured && <span style={{ fontSize: 9, color: '#8b5cf6' }}>✓</span>}
                      {storageMode === 'minio' && <span style={{ position: 'absolute', top: -3, right: -3, width: 7, height: 7, borderRadius: '50%', background: '#8b5cf6', border: '1.5px solid var(--modal-surface)' }} />}
                    </button>
                  </div>
                </div>
              </div>

              {/* 右侧面板 */}
              <div className="custom-scrollbar" style={rightPanel}>
                {!activeSelection ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)' }}>
                    <Settings size={48} color="#475569" style={{ marginBottom: 16 }} />
                    <p style={{ margin: 0 }}>← 请在左侧选择配置项</p>
                  </div>
                ) : activeSelection.type === 'fonts' ? (
                  <FontsPanel
                    adminFonts={adminFonts}
                    setAdminFonts={setAdminFonts}
                    fontForm={fontForm}
                    setFontForm={setFontForm}
                    fontUploading={fontUploading}
                    setFontUploading={setFontUploading}
                    fontFileRef={fontFileRef}
                    showToast={showToast}
                    labelStyle={labelStyle}
                    inputStyle={inputStyle}
                    btn={btn}
                  />
                ) : activeSelection.type === 'voice' ? (
                  // 🎤 语音识别 (ASR) 多账号配置面板
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ paddingBottom: 12, borderBottom: '1px solid var(--border-subtle)' }}>
                      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--text-body)', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Mic size={18} />
                        语音识别配置
                      </h3>
                      <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
                        配置火山引擎豆包语音识别 API，支持多账号负载均衡
                      </p>
                      {asrInstances.length > 0 && (
                        <div style={{
                          marginTop: 8, padding: '6px 10px', borderRadius: 6,
                          background: 'rgba(168,85,247,0.08)', border: '1px solid rgba(168,85,247,0.15)',
                          display: 'flex', alignItems: 'center', gap: 12, fontSize: 12, color: '#c4b5fd'
                        }}>
                          <span>已配置 <b style={{ color: '#e9d5ff' }}>{asrInstances.length}</b> 个账号</span>
                          <span style={{ color: 'var(--border-strong)' }}>|</span>
                          <span>总并发 <b style={{ color: '#e9d5ff' }}>{asrInstances.reduce((s, i) => s + (Number(i.max_concurrency) || 2), 0)}</b></span>
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <button
                        onClick={() => {
                          setAsrInstances(prev => [...prev, { label: '', app_id: '', access_token: '', max_concurrency: 2 }]);
                          setSelectedAsrIdx(asrInstances.length);
                          setShowAsrToken(false);
                        }}
                        title="添加账号"
                        style={{
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          width: 32, height: 32, borderRadius: 6, flexShrink: 0,
                          border: '1px solid rgba(168,85,247,0.3)', background: 'rgba(168,85,247,0.1)',
                          color: '#c084fc', cursor: 'pointer', transition: 'all 0.15s'
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(168,85,247,0.2)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'rgba(168,85,247,0.1)'; }}
                      >
                        <Plus size={16} />
                      </button>
                      <select
                        value={selectedAsrIdx}
                        onChange={e => { setSelectedAsrIdx(Number(e.target.value)); setShowAsrToken(false); }}
                        style={{ ...inputStyle, flex: 1, cursor: 'pointer', appearance: 'auto', WebkitAppearance: 'auto' }}
                        disabled={asrInstances.length === 0}
                      >
                        {asrInstances.length === 0 && <option>暂无账号，请点击 + 添加</option>}
                        {asrInstances.map((inst, i) => (
                          <option key={i} value={i}>
                            {inst.label || `账号 ${i + 1}`}{inst.app_id ? ` (${inst.app_id.substring(0, 6)}...)` : ''}
                          </option>
                        ))}
                      </select>
                      {asrInstances.length > 0 && (
                        <button
                          onClick={() => {
                            setAsrInstances(prev => prev.filter((_, idx) => idx !== selectedAsrIdx));
                            setSelectedAsrIdx(prev => Math.max(0, Math.min(prev, asrInstances.length - 2)));
                            setShowAsrToken(false);
                          }}
                          title="删除当前账号"
                          style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: 32, height: 32, borderRadius: 6, flexShrink: 0,
                            border: '1px solid var(--border-subtle)', background: 'transparent',
                            color: 'var(--text-muted)', cursor: 'pointer', transition: 'all 0.15s'
                          }}
                          onMouseEnter={e => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.borderColor = 'rgba(239,68,68,0.3)'; }}
                          onMouseLeave={e => { e.currentTarget.style.color = 'var(--text-muted)'; e.currentTarget.style.borderColor = 'var(--border-subtle)'; }}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>

                    {asrInstances.length > 0 && (() => {
                      const idx = Math.min(selectedAsrIdx, asrInstances.length - 1);
                      const inst = asrInstances[idx];
                      if (!inst) return null;
                      const update = (field, value) => setAsrInstances(prev => prev.map((item, i) => i === idx ? { ...item, [field]: value } : item));
                      return (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16, background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)', borderRadius: 8 }}>
                          <div>
                            <label style={labelStyle}>账号名称</label>
                            <input style={inputStyle} value={inst.label || ''} onChange={e => update('label', e.target.value)} placeholder={`账号 ${idx + 1}`} />
                          </div>
                          <div>
                            <label style={labelStyle}>App ID（应用 ID）*</label>
                            <input style={inputStyle} value={inst.app_id || ''} onChange={e => update('app_id', e.target.value)} placeholder="例如：6307332770" />
                            <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>在火山引擎控制台 → 语音技术 → 应用管理中获取</div>
                          </div>
                          <div>
                            <label style={labelStyle}>Access Token（鉴权令牌）*</label>
                            <div style={{ position: 'relative' }}>
                              <input style={{ ...inputStyle, paddingRight: 36 }} type={showAsrToken ? 'text' : 'password'} value={inst.access_token || ''} onChange={e => update('access_token', e.target.value)} placeholder="火山引擎语音服务鉴权 Token" />
                              <button onClick={() => setShowAsrToken(!showAsrToken)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                                {showAsrToken ? <EyeOff size={14} /> : <Eye size={14} />}
                              </button>
                            </div>
                            <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>在控制台 → 语音技术 → 流式语音识别 → 鉴权信息中获取</div>
                          </div>
                          <div>
                            <label style={labelStyle}>Resource ID（资源标识）</label>
                            <input style={{ ...inputStyle, opacity: 0.6, cursor: 'not-allowed' }} value="volc.bigasr.sauc.duration" readOnly />
                            <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>豆包语音识别 bigmodel 双向流式（固定值，无需修改）</div>
                          </div>
                          <div>
                            <label style={labelStyle}>最大并发数</label>
                            <input type="number" min="1" style={{ ...inputStyle, width: 100 }} value={inst.max_concurrency ?? 2} onChange={e => update('max_concurrency', Math.max(1, parseInt(e.target.value) || 1))} />
                            <div style={{ fontSize: 11, color: '#475569', marginTop: 4 }}>该账号允许同时进行的语音识别连接数</div>
                          </div>
                        </div>
                      );
                    })()}

                    <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                      <button
                        style={btn(true)}
                        disabled={asrSaving || asrInstances.length === 0}
                        onClick={async () => {
                          setAsrSaving(true);
                          try {
                            await api.post('/api/settings/voice-asr/instances', { instances: asrInstances });
                            setAsrConfigured(asrInstances.length > 0);
                            showToast('✅ 语音识别配置已保存', 'success', 3000);
                          } catch (err) {
                            showToast('❌ 保存失败: ' + (err.message || '未知错误'), 'warning');
                          } finally {
                            setAsrSaving(false);
                          }
                        }}
                      >
                        {asrSaving ? '保存中...' : '💾 保存配置'}
                      </button>

                      <button
                        style={btn(false)}
                        disabled={asrTesting || asrInstances.length === 0}
                        onClick={async () => {
                          const idx = Math.min(selectedAsrIdx, asrInstances.length - 1);
                          const inst = asrInstances[idx];
                          if (!inst?.app_id || !inst?.access_token) {
                            showToast('⚠️ 请先填写当前账号的 App ID 和 Access Token', 'warning');
                            return;
                          }
                          setAsrTesting(true);
                          try {
                            const result = await api.post('/api/settings/voice-asr/test', {
                              appId: inst.app_id,
                              accessToken: inst.access_token,
                            });
                            if (result.success) {
                              showToast('✅ ' + (result.message || '连接成功'), 'success');
                            } else {
                              showToast('❌ ' + (result.message || '连接失败'), 'warning');
                            }
                          } catch (err) {
                            showToast('❌ 测试失败: ' + (err.message || '未知错误'), 'warning');
                          } finally {
                            setAsrTesting(false);
                          }
                        }}
                      >
                        {asrTesting ? '测试中...' : '🔊 测试连接'}
                      </button>
                    </div>

                    <div style={{
                      marginTop: 8, padding: 12,
                      background: 'rgba(168,85,247,0.08)',
                      border: '1px solid rgba(168,85,247,0.2)',
                      borderRadius: 8, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6,
                    }}>
                      <div style={{ fontWeight: 600, color: '#c084fc', marginBottom: 6 }}>📋 配置说明</div>
                      <p style={{ margin: '0 0 6px' }}>• 本功能使用<b>火山引擎豆包流式语音识别 2.0</b>接口</p>
                      <p style={{ margin: '0 0 6px' }}>• 配置完成后，所有文本输入框旁会出现🎤按钮</p>
                      <p style={{ margin: '0 0 6px' }}>• 可添加多个账号，系统自动进行负载均衡（并发满时切换下一个）</p>
                      <p style={{ margin: '0 0 6px' }}>• 获取方式：登录
                        <a href="https://console.volcengine.com/speech/app" target="_blank" rel="noreferrer" style={{ color: '#c084fc', textDecoration: 'underline' }}>
                          火山引擎控制台
                        </a>
                        → 语音技术 → 应用管理
                      </p>
                      <p style={{ margin: 0 }}>• 需要开通「流式语音识别」服务并创建应用</p>
                    </div>
                  </div>
                ) : activeSelection.type === 'oss' ? (
                  // OSS 配置表单
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ paddingBottom: 12, borderBottom: '1px solid var(--border-subtle)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--text-body)', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Database size={18} />
                          阿里云 OSS 配置
                        </h3>
                        {ossConfigured && (
                          <button
                            onClick={() => handleSwitchStorageMode(storageMode === 'oss' ? 'local' : 'oss')}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6,
                              padding: '5px 12px', borderRadius: 20,
                              border: `1px solid ${storageMode === 'oss' ? '#10b981' : 'var(--border-strong)'}`,
                              background: storageMode === 'oss' ? 'rgba(16,185,129,0.15)' : 'transparent',
                              color: storageMode === 'oss' ? '#34d399' : 'var(--text-muted)',
                              cursor: 'pointer', fontSize: 12, fontWeight: 500, transition: 'all 0.2s'
                            }}
                          >
                            {storageMode === 'oss' ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                            {storageMode === 'oss' ? '已启用' : '未启用'}
                          </button>
                        )}
                      </div>
                      <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
                        配置 OSS 后，图片/视频/音频将存储到云端，服务器仅保存小文件（头像/Logo/缩略图）
                      </p>
                      {storageMode === 'r2' && ossConfigured && (
                        <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 6, background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.2)', fontSize: 11, color: '#fbbf24' }}>
                          ⚠️ 当前正在使用 R2 存储。启用 OSS 将自动关闭 R2（历史文件不受影响）
                        </div>
                      )}
                    </div>

                    <div>
                      <label style={labelStyle}>Region（地域）*</label>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
                        {[
                          { value: 'oss-cn-beijing', label: '华北2（北京）' },
                          { value: 'oss-cn-hangzhou', label: '华东1（杭州）' },
                          { value: 'oss-cn-shanghai', label: '华东2（上海）' },
                          { value: 'oss-cn-shenzhen', label: '华南1（深圳）' },
                          { value: 'oss-cn-guangzhou', label: '华南2（广州）' },
                          { value: 'oss-cn-qingdao', label: '华北1（青岛）' },
                          { value: 'oss-cn-zhangjiakou', label: '华北3（张家口）' },
                          { value: 'oss-cn-chengdu', label: '西南1（成都）' },
                          { value: 'oss-cn-hongkong', label: '中国香港' },
                        ].map(region => {
                          const isActive = ossConfig.region === region.value;
                          return (
                            <button
                              key={region.value}
                              onClick={() => handleOssConfigChange('region', region.value)}
                              style={{
                                padding: '8px 10px',
                                background: isActive ? 'rgba(16,185,129,0.2)' : 'var(--modal-input-bg)',
                                border: `1px solid ${isActive ? '#10b981' : 'var(--border-subtle)'}`,
                                borderRadius: 6, cursor: 'pointer', transition: 'all 0.15s',
                                fontSize: 11, color: isActive ? '#34d399' : 'var(--text-secondary)',
                                fontWeight: isActive ? 600 : 400, textAlign: 'center'
                              }}
                            >
                              {region.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div>
                      <label style={labelStyle}>素材桶名称 *（永久存储）</label>
                      <input type="text" style={inputStyle} value={ossConfig.mediaBucket} onChange={(e) => handleOssConfigChange('mediaBucket', e.target.value)} placeholder="例如：cineart-canvas" />
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>存储用户上传的图片/视频/音频和 AI 生成结果，不会自动删除</div>
                    </div>

                    <div>
                      <label style={labelStyle}>临时桶名称（短期存储，可选）</label>
                      <input type="text" style={inputStyle} value={ossConfig.refBucket} onChange={(e) => handleOssConfigChange('refBucket', e.target.value)} placeholder="例如：cineart-ref（留空则与素材桶相同）" />
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>存储聊天语音等临时文件，建议配置 7 天生命周期规则自动清理</div>
                    </div>

                    <div>
                      <label style={labelStyle}>Access Key ID *</label>
                      <div style={{ position: 'relative' }}>
                        <input type={showOssKeyId ? 'text' : 'password'} style={{ ...inputStyleSmall, paddingRight: 40 }} value={ossConfig.accessKeyId} onChange={(e) => handleOssConfigChange('accessKeyId', e.target.value)} placeholder="LTAI..." />
                        <button onClick={() => setShowOssKeyId(!showOssKeyId)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}>
                          {showOssKeyId ? <EyeOff size={16} color="var(--text-secondary)" /> : <Eye size={16} color="var(--text-secondary)" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label style={labelStyle}>Access Key Secret *</label>
                      <div style={{ position: 'relative' }}>
                        <input type={showOssKeySecret ? 'text' : 'password'} style={{ ...inputStyleSmall, paddingRight: 40 }} value={ossConfig.accessKeySecret} onChange={(e) => handleOssConfigChange('accessKeySecret', e.target.value)} placeholder={ossConfigured && !ossConfig.accessKeySecret ? '••••••••••••••••（已保存）' : '输入 Secret'} />
                        <button onClick={() => setShowOssKeySecret(!showOssKeySecret)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}>
                          {showOssKeySecret ? <EyeOff size={16} color="var(--text-secondary)" /> : <Eye size={16} color="var(--text-secondary)" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label style={labelStyle}>自定义域名（可选）</label>
                      <input type="text" style={inputStyle} value={ossConfig.endpoint} onChange={(e) => handleOssConfigChange('endpoint', e.target.value)} placeholder="例如：cdn.example.com" />
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>如已绑定自定义域名，填写此项。留空则使用默认 OSS 域名</div>
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={handleOssSave} disabled={saving} style={{ ...btn(true), flex: 1, opacity: saving ? 0.5 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
                        {saving ? '保存中...' : '💾 保存配置'}
                      </button>
                      <button onClick={handleOssTest} disabled={ossTesting} style={{ ...btn(false), opacity: ossTesting ? 0.5 : 1, cursor: ossTesting ? 'not-allowed' : 'pointer' }}>
                        {ossTesting ? '测试中...' : '🔍 测试连接'}
                      </button>
                    </div>

                    <div style={{ padding: 12, background: 'rgba(59,130,246,0.1)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                      <div style={{ fontWeight: 600, color: '#60a5fa', marginBottom: 4 }}>💡 配置说明</div>
                      <ol style={{ margin: '4px 0', paddingLeft: 20 }}>
                        <li>在阿里云控制台创建两个 Bucket（或只用一个）</li>
                        <li><b>素材桶</b>：存储用户上传的媒体文件和 AI 生成结果，设为「公共读」</li>
                        <li><b>临时桶</b>：存储聊天语音等临时文件，建议配置 7 天自动删除规则</li>
                        <li>两个桶使用相同的 AccessKey 和 Region</li>
                        <li>如果只填素材桶，临时桶默认使用同一个桶</li>
                      </ol>
                    </div>
                  </div>
                ) : activeSelection.type === 'r2' ? (
                  // ☁️ Cloudflare R2 配置表单
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ paddingBottom: 12, borderBottom: '1px solid var(--border-subtle)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--text-body)', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Cloud size={18} />
                          Cloudflare R2 配置
                        </h3>
                        {r2Configured && (
                          <button
                            onClick={() => handleSwitchStorageMode(storageMode === 'r2' ? 'local' : 'r2')}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6,
                              padding: '5px 12px', borderRadius: 20,
                              border: `1px solid ${storageMode === 'r2' ? '#f97316' : 'var(--border-strong)'}`,
                              background: storageMode === 'r2' ? 'rgba(249,115,22,0.15)' : 'transparent',
                              color: storageMode === 'r2' ? '#fb923c' : 'var(--text-muted)',
                              cursor: 'pointer', fontSize: 12, fontWeight: 500, transition: 'all 0.2s'
                            }}
                          >
                            {storageMode === 'r2' ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                            {storageMode === 'r2' ? '已启用' : '未启用'}
                          </button>
                        )}
                      </div>
                      <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
                        配置 R2 后，图片/视频/音频将存储到 Cloudflare 全球边缘网络
                      </p>
                      {storageMode === 'oss' && r2Configured && (
                        <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 6, background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.2)', fontSize: 11, color: '#fbbf24' }}>
                          ⚠️ 当前正在使用 OSS 存储。启用 R2 将自动关闭 OSS（历史文件不受影响）
                        </div>
                      )}
                    </div>

                    <div>
                      <label style={labelStyle}>账户 ID *</label>
                      <input type="text" style={inputStyle} value={r2Config.accountId} onChange={(e) => handleR2ConfigChange('accountId', e.target.value)} placeholder="Cloudflare 仪表板 → R2 → 概述页面复制" />
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>在 Cloudflare 仪表板 → R2 对象存储 → 概述 页面获取</div>
                    </div>

                    <div>
                      <label style={labelStyle}>素材桶名称 *（永久存储）</label>
                      <input type="text" style={inputStyle} value={r2Config.bucket} onChange={(e) => handleR2ConfigChange('bucket', e.target.value)} placeholder="例如：cineart-media" />
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>存储用户上传的图片/视频/音频和 AI 生成结果，不会自动删除</div>
                    </div>

                    <div>
                      <label style={labelStyle}>临时桶名称（短期存储，可选）</label>
                      <input type="text" style={inputStyle} value={r2Config.refBucket} onChange={(e) => handleR2ConfigChange('refBucket', e.target.value)} placeholder="例如：cineart-ref（留空则与素材桶相同）" />
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>存储聊天语音消息等临时文件，建议配置生命周期规则自动清理</div>
                    </div>

                    <div>
                      <label style={labelStyle}>访问密钥 ID *</label>
                      <div style={{ position: 'relative' }}>
                        <input type={showR2KeyId ? 'text' : 'password'} style={{ ...inputStyleSmall, paddingRight: 40 }} value={r2Config.accessKeyId} onChange={(e) => handleR2ConfigChange('accessKeyId', e.target.value)} placeholder="创建 R2 API 令牌后获得" />
                        <button onClick={() => setShowR2KeyId(!showR2KeyId)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}>
                          {showR2KeyId ? <EyeOff size={16} color="var(--text-secondary)" /> : <Eye size={16} color="var(--text-secondary)" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label style={labelStyle}>机密访问密钥 *</label>
                      <div style={{ position: 'relative' }}>
                        <input type={showR2KeySecret ? 'text' : 'password'} style={{ ...inputStyleSmall, paddingRight: 40 }} value={r2Config.secretAccessKey} onChange={(e) => handleR2ConfigChange('secretAccessKey', e.target.value)} placeholder={r2Configured && !r2Config.secretAccessKey ? '••••••••••••••••（已保存）' : '创建 R2 API 令牌后获得'} />
                        <button onClick={() => setShowR2KeySecret(!showR2KeySecret)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}>
                          {showR2KeySecret ? <EyeOff size={16} color="var(--text-secondary)" /> : <Eye size={16} color="var(--text-secondary)" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label style={labelStyle}>素材桶自定义域名（推荐）</label>
                      <input type="text" style={inputStyle} value={r2Config.publicDomain} onChange={(e) => handleR2ConfigChange('publicDomain', e.target.value)} placeholder="例如：media.example.com" />
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>素材桶的自定义域名，用于 AI 生成结果和上传文件的访问</div>
                    </div>

                    <div>
                      <label style={labelStyle}>临时桶自定义域名</label>
                      <input type="text" style={inputStyle} value={r2Config.refPublicDomain} onChange={(e) => handleR2ConfigChange('refPublicDomain', e.target.value)} placeholder="例如：ref.example.com（留空则同素材桶域名）" />
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>临时桶（聊天语音等）的自定义域名。留空则复用素材桶域名；若临时桶和素材桶相同无需填写</div>
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={handleR2Save} disabled={saving} style={{ ...btn(true), flex: 1, background: '#f97316', borderColor: '#f97316', opacity: saving ? 0.5 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
                        {saving ? '保存中...' : '💾 保存配置'}
                      </button>
                      <button onClick={handleR2Test} disabled={r2Testing} style={{ ...btn(false), opacity: r2Testing ? 0.5 : 1, cursor: r2Testing ? 'not-allowed' : 'pointer' }}>
                        {r2Testing ? '测试中...' : '🔍 测试连接'}
                      </button>
                    </div>

                    <div style={{ padding: 12, background: 'rgba(249,115,22,0.08)', border: '1px solid rgba(249,115,22,0.2)', borderRadius: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                      <div style={{ fontWeight: 600, color: '#fb923c', marginBottom: 4 }}>💡 配置说明</div>
                      <ol style={{ margin: '4px 0', paddingLeft: 20 }}>
                        <li>在 Cloudflare 仪表板创建两个 R2 存储桶（或只用一个），并开启公共访问</li>
                        <li><b>素材桶</b>：存储用户上传的媒体文件和 AI 生成结果</li>
                        <li><b>临时桶</b>：存储聊天语音等临时文件，建议配置生命周期规则自动删除</li>
                        <li>创建 R2 API 令牌：R2 → 管理 R2 API 令牌 → 创建 API 令牌</li>
                        <li>权限选择「对象读和写」，记录「访问密钥 ID」和「机密访问密钥」</li>
                        <li>如果只填素材桶，临时桶默认使用同一个桶</li>
                        <li>R2 免费额度：10GB 存储 + 每月 1000 万次读取</li>
                      </ol>
                    </div>
                  </div>
                ) : activeSelection.type === 'minio' ? (
                  // 🗄️ 自建 MinIO 配置表单（教育版 / 数据不出校）
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ paddingBottom: 12, borderBottom: '1px solid var(--border-subtle)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--text-body)', display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Database size={18} />
                          自建 MinIO 配置
                        </h3>
                        {minioConfigured && (
                          <button
                            onClick={() => handleSwitchStorageMode(storageMode === 'minio' ? 'local' : 'minio')}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 6,
                              padding: '5px 12px', borderRadius: 20,
                              border: `1px solid ${storageMode === 'minio' ? '#8b5cf6' : 'var(--border-strong)'}`,
                              background: storageMode === 'minio' ? 'rgba(139,92,246,0.15)' : 'transparent',
                              color: storageMode === 'minio' ? '#a78bfa' : 'var(--text-muted)',
                              cursor: 'pointer', fontSize: 12, fontWeight: 500, transition: 'all 0.2s'
                            }}
                          >
                            {storageMode === 'minio' ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                            {storageMode === 'minio' ? '已启用' : '未启用'}
                          </button>
                        )}
                      </div>
                      <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
                        配置后，图片/视频/音频存到自建服务器（数据不出本地机房，不经过任何公有云）
                      </p>
                      {storageMode !== 'minio' && storageMode !== 'local' && minioConfigured && (
                        <div style={{ marginTop: 8, padding: '6px 10px', borderRadius: 6, background: 'rgba(234,179,8,0.1)', border: '1px solid rgba(234,179,8,0.2)', fontSize: 11, color: '#fbbf24' }}>
                          ⚠️ 当前正在使用 {STORAGE_BADGE[storageMode]?.label || storageMode} 存储。启用 MinIO 将自动关闭它（历史文件不受影响）
                        </div>
                      )}
                    </div>

                    <div>
                      <label style={labelStyle}>服务地址（Endpoint）*</label>
                      <input type="text" style={inputStyle} value={minioConfig.endpoint} onChange={(e) => handleMinioConfigChange('endpoint', e.target.value)} placeholder="例如：https://storage.school.edu.cn 或 http://192.168.1.15:9000" />
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>后端直连 MinIO 的 S3 地址，必须带协议；不要带桶名</div>
                    </div>

                    <div>
                      <label style={labelStyle}>素材桶名称 *（永久存储）</label>
                      <input type="text" style={inputStyle} value={minioConfig.bucket} onChange={(e) => handleMinioConfigChange('bucket', e.target.value)} placeholder="例如：cineart-media" />
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>存储用户上传的图片/视频/音频和 AI 生成结果，不会自动删除</div>
                    </div>

                    <div>
                      <label style={labelStyle}>临时桶名称（短期存储，可选）</label>
                      <input type="text" style={inputStyle} value={minioConfig.refBucket} onChange={(e) => handleMinioConfigChange('refBucket', e.target.value)} placeholder="例如：cineart-ref（留空则与素材桶相同）" />
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>存储聊天语音、参考图等临时文件，建议配生命周期规则自动清理</div>
                    </div>

                    <div>
                      <label style={labelStyle}>访问密钥 ID *</label>
                      <div style={{ position: 'relative' }}>
                        <input type={showMinioKeyId ? 'text' : 'password'} style={{ ...inputStyleSmall, paddingRight: 40 }} value={minioConfig.accessKeyId} onChange={(e) => handleMinioConfigChange('accessKeyId', e.target.value)} placeholder="MinIO 控制台创建的服务账号 Access Key" />
                        <button onClick={() => setShowMinioKeyId(!showMinioKeyId)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}>
                          {showMinioKeyId ? <EyeOff size={16} color="var(--text-secondary)" /> : <Eye size={16} color="var(--text-secondary)" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label style={labelStyle}>机密访问密钥 *</label>
                      <div style={{ position: 'relative' }}>
                        <input type={showMinioKeySecret ? 'text' : 'password'} style={{ ...inputStyleSmall, paddingRight: 40 }} value={minioConfig.secretAccessKey} onChange={(e) => handleMinioConfigChange('secretAccessKey', e.target.value)} placeholder={minioConfigured && !minioConfig.secretAccessKey ? '••••••••••••••••（已保存）' : '服务账号 Secret Key'} />
                        <button onClick={() => setShowMinioKeySecret(!showMinioKeySecret)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center' }}>
                          {showMinioKeySecret ? <EyeOff size={16} color="var(--text-secondary)" /> : <Eye size={16} color="var(--text-secondary)" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label style={labelStyle}>素材桶公开域名 *（必须带桶名）</label>
                      <input type="text" style={inputStyle} value={minioConfig.publicDomain} onChange={(e) => handleMinioConfigChange('publicDomain', e.target.value)} placeholder="例如：storage.school.edu.cn/cineart-media" />
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>MinIO 是路径风格，公开地址形如 域名/桶名/文件路径，所以这里要把桶名一起写上</div>
                    </div>

                    <div>
                      <label style={labelStyle}>临时桶公开域名</label>
                      <input type="text" style={inputStyle} value={minioConfig.refPublicDomain} onChange={(e) => handleMinioConfigChange('refPublicDomain', e.target.value)} placeholder="例如：storage.school.edu.cn/cineart-ref（留空则同素材桶）" />
                      <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 4 }}>同样要带桶名。若临时桶和素材桶相同则无需填写</div>
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={handleMinioSave} disabled={saving} style={{ ...btn(true), flex: 1, background: '#8b5cf6', borderColor: '#8b5cf6', opacity: saving ? 0.5 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
                        {saving ? '保存中...' : '💾 保存配置'}
                      </button>
                      <button onClick={handleMinioTest} disabled={minioTesting} style={{ ...btn(false), opacity: minioTesting ? 0.5 : 1, cursor: minioTesting ? 'not-allowed' : 'pointer' }}>
                        {minioTesting ? '测试中...' : '🔍 测试连接'}
                      </button>
                    </div>

                    <div style={{ padding: 12, background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.2)', borderRadius: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
                      <div style={{ fontWeight: 600, color: '#a78bfa', marginBottom: 4 }}>💡 配置说明</div>
                      <ol style={{ margin: '4px 0', paddingLeft: 20 }}>
                        <li>在 MinIO 控制台建两个桶（或只用一个），素材桶设<b>匿名只读</b>策略（AI 厂商要能拉参考图）</li>
                        <li>匿名策略只给 <code>s3:GetObject</code>，<b>不要给 ListBucket</b>，否则整桶目录会被公开列出</li>
                        <li>用「服务账号」而不是 root 密钥：控制台 → Access Keys → Create，权限只给这两个桶读写</li>
                        <li>反代（Nginx）必须<b>纯透传，不能 rewrite 路径</b> —— 预签名签的就是路径，改一下签名就对不上</li>
                        <li>公开域名要写成 <code>域名/桶名</code>，让预签名上传和公开访问落在同一条路径上</li>
                        <li>MinIO 不支持 <code>x-oss-process</code>，缩略图/分辨率改由生成时落文件，无需额外配置</li>
                      </ol>
                    </div>
                  </div>
                ) : activeSelection.type === 'categories' ? (
                  // 📚 资产库配置面板（内嵌显示）
                  <CategoryManagement embedded={true} />
                ) : (HAS_PAYMENT && activeSelection.type === 'payment') ? (
                  // 💳 支付配置面板（教育版：process.env 是构建期字面量替换，terser 直接删掉整个分支）
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ paddingBottom: 12, borderBottom: '1px solid var(--border-subtle)' }}>
                      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--text-body)', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CreditCard size={18} style={{ color: '#eab308' }} />
                        充值支付配置
                      </h3>
                      <p style={{ margin: '4px 0 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
                        配置虎皮椒支付接口，启用用户充值功能（微信支付）
                      </p>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>APPID</label>
                      <input type="text" value={paymentConfig.appid} onChange={e => setPaymentConfig(prev => ({ ...prev, appid: e.target.value }))} placeholder="虎皮椒商户 APPID" style={{ width: '100%', padding: '8px 12px', background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)', borderRadius: 6, color: 'var(--text-body)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>APPSECRET</label>
                      <div style={{ position: 'relative' }}>
                        <input type={showPaymentSecret ? 'text' : 'password'} value={paymentConfig.appsecret} onChange={e => setPaymentConfig(prev => ({ ...prev, appsecret: e.target.value }))} placeholder="虎皮椒商户密钥" style={{ width: '100%', padding: '8px 12px', paddingRight: 36, background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)', borderRadius: 6, color: 'var(--text-body)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                        <button onClick={() => setShowPaymentSecret(!showPaymentSecret)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }}>
                          {showPaymentSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>API 网关地址</label>
                      <input type="text" value={paymentConfig.api_url} onChange={e => setPaymentConfig(prev => ({ ...prev, api_url: e.target.value }))} placeholder="https://api.xunhupay.com/payment/do.html" style={{ width: '100%', padding: '8px 12px', background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)', borderRadius: 6, color: 'var(--text-body)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                      <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--text-muted)' }}>备用域名：https://api.dpweixin.com/payment/do.html</p>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>扫码页提示语</label>
                      <input type="text" value={paymentConfig.qr_hint} onChange={e => setPaymentConfig(prev => ({ ...prev, qr_hint: e.target.value }))} placeholder="请使用微信扫码支付" style={{ width: '100%', padding: '8px 12px', background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)', borderRadius: 6, color: 'var(--text-body)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                      <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--text-muted)' }}>用户扫码支付时显示的提示文字（留空则显示默认值）</p>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>兑换比例（1 元 = ? 积分）</label>
                      <input type="number" min="1" value={paymentConfig.rate} onChange={e => setPaymentConfig(prev => ({ ...prev, rate: Math.max(1, parseInt(e.target.value) || 1) }))} style={{ width: 120, padding: '8px 12px', background: 'var(--modal-input-bg)', border: '1px solid rgba(234,179,8,0.3)', borderRadius: 6, color: '#facc15', fontSize: 13, outline: 'none', fontWeight: 600 }} />
                      <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--text-muted)' }}>例如填 10 表示用户充值 ¥1 获得 10 积分</p>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 8, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>充值套餐</label>
                      <div style={{ border: '1px solid var(--border-subtle)', borderRadius: 8, overflow: 'hidden', marginBottom: 8 }}>
                        <div style={{ display: 'flex', padding: '6px 10px', background: 'var(--modal-input-bg)', borderBottom: '1px solid var(--modal-input-border)' }}>
                          <span style={{ flex: 1, fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>金额（元）</span>
                          <span style={{ flex: 1, fontSize: 11, color: 'var(--text-muted)', fontWeight: 600 }}>积分（空=按汇率计算）</span>
                          <span style={{ width: 28 }}></span>
                        </div>
                        {(paymentConfig.packages || []).sort((a, b) => {
                          const aAmt = typeof a === 'object' ? a.amount : a;
                          const bAmt = typeof b === 'object' ? b.amount : b;
                          return aAmt - bAmt;
                        }).map((pkg, idx) => {
                          const pkgAmount = typeof pkg === 'object' ? pkg.amount : pkg;
                          const pkgCredits = typeof pkg === 'object' ? pkg.credits : null;
                          const calcCredits = Math.round(pkgAmount * (paymentConfig.rate || 10));
                          return (
                            <div key={pkgAmount} style={{
                              display: 'flex', alignItems: 'center', padding: '5px 10px',
                              background: idx % 2 === 0 ? 'transparent' : 'var(--modal-input-bg)',
                              borderBottom: '1px solid var(--modal-input-border)'
                            }}>
                              <span style={{ flex: 1, fontSize: 13, color: '#facc15', fontWeight: 600 }}>¥{pkgAmount}</span>
                              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <input
                                  type="number" min="1"
                                  value={pkgCredits || ''}
                                  onChange={e => {
                                    const newCredits = e.target.value ? parseInt(e.target.value) : null;
                                    setPaymentConfig(prev => ({
                                      ...prev,
                                      packages: prev.packages.map(p => {
                                        const pAmt = typeof p === 'object' ? p.amount : p;
                                        if (pAmt === pkgAmount) return { amount: pkgAmount, credits: newCredits };
                                        return typeof p === 'object' ? p : { amount: p, credits: null };
                                      })
                                    }));
                                  }}
                                  placeholder={String(calcCredits)}
                                  style={{
                                    width: 80, padding: '3px 8px', background: 'rgba(0,0,0,0.2)',
                                    border: `1px solid ${pkgCredits ? 'rgba(234,179,8,0.4)' : 'var(--border-subtle)'}`,
                                    borderRadius: 4, color: pkgCredits ? '#facc15' : 'var(--text-muted)', fontSize: 12,
                                    outline: 'none', fontWeight: pkgCredits ? 600 : 400,
                                  }}
                                />
                                {pkgCredits && pkgCredits !== calcCredits && (
                                  <span style={{ fontSize: 10, color: pkgCredits > calcCredits ? '#10b981' : '#f97316', whiteSpace: 'nowrap' }}>
                                    {pkgCredits > calcCredits ? '↑多送' : '↓折扣'}
                                  </span>
                                )}
                              </div>
                              <button
                                onClick={() => setPaymentConfig(prev => ({
                                  ...prev,
                                  packages: prev.packages.filter(p => {
                                    const pAmt = typeof p === 'object' ? p.amount : p;
                                    return pAmt !== pkgAmount;
                                  })
                                }))}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 14, padding: '0 2px', lineHeight: 1, width: 28, textAlign: 'center' }}
                              >×</button>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <input
                          type="number" min="1"
                          value={newPackageAmount}
                          onChange={e => setNewPackageAmount(e.target.value)}
                          placeholder="金额"
                          style={{ width: 80, padding: '6px 10px', background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)', borderRadius: 6, color: 'var(--text-body)', fontSize: 12, outline: 'none' }}
                          onKeyDown={e => {
                            if (e.key === 'Enter') {
                              const val = parseInt(newPackageAmount);
                              const existing = (paymentConfig.packages || []).find(p => { const pAmt = typeof p === 'object' ? p.amount : p; return pAmt === val; });
                              if (val > 0 && !existing) {
                                setPaymentConfig(prev => ({ ...prev, packages: [...prev.packages, { amount: val, credits: null }] }));
                                setNewPackageAmount('');
                              }
                            }
                          }}
                        />
                        <button
                          onClick={() => {
                            const val = parseInt(newPackageAmount);
                            const existing = (paymentConfig.packages || []).find(p => { const pAmt = typeof p === 'object' ? p.amount : p; return pAmt === val; });
                            if (val > 0 && !existing) {
                              setPaymentConfig(prev => ({ ...prev, packages: [...prev.packages, { amount: val, credits: null }] }));
                              setNewPackageAmount('');
                            }
                          }}
                          style={{ padding: '6px 12px', background: 'rgba(234,179,8,0.15)', border: '1px solid rgba(234,179,8,0.3)', borderRadius: 6, cursor: 'pointer', color: '#facc15', fontSize: 12 }}
                        >添加</button>
                        <span style={{ fontSize: 10, color: '#475569' }}>积分留空则按汇率自动计算</span>
                      </div>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <button onClick={() => setPaymentConfig(prev => ({ ...prev, custom_enabled: !prev.custom_enabled }))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: paymentConfig.custom_enabled ? '#eab308' : '#475569' }}>
                        {paymentConfig.custom_enabled ? <ToggleRight size={22} /> : <ToggleLeft size={22} />}
                      </button>
                      <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>允许用户自定义金额</span>
                      {paymentConfig.custom_enabled && (
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>（¥{paymentConfig.custom_min} ~ ¥{paymentConfig.custom_max}）</span>
                      )}
                    </div>
                    {paymentConfig.custom_enabled && (
                      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <div>
                          <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>最小金额</label>
                          <input type="number" min="1" value={paymentConfig.custom_min} onChange={e => setPaymentConfig(prev => ({ ...prev, custom_min: Math.max(1, parseInt(e.target.value) || 1) }))} style={{ width: 80, padding: '6px 10px', background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)', borderRadius: 6, color: 'var(--text-body)', fontSize: 12, outline: 'none', marginTop: 2, display: 'block' }} />
                        </div>
                        <span style={{ color: '#475569', marginTop: 16 }}>~</span>
                        <div>
                          <label style={{ fontSize: 11, color: 'var(--text-muted)' }}>最大金额</label>
                          <input type="number" min="1" value={paymentConfig.custom_max} onChange={e => setPaymentConfig(prev => ({ ...prev, custom_max: Math.max(1, parseInt(e.target.value) || 1) }))} style={{ width: 80, padding: '6px 10px', background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)', borderRadius: 6, color: 'var(--text-body)', fontSize: 12, outline: 'none', marginTop: 2, display: 'block' }} />
                        </div>
                      </div>
                    )}

                    <div style={{ paddingTop: 12, borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 8 }}>
                      <button
                        onClick={async () => {
                          if (!paymentConfig.appid || !paymentConfig.appsecret) {
                            showToast('请填写 APPID 和 APPSECRET', 'warning', 4000);
                            return;
                          }
                          setPaymentSaving(true);
                          try {
                            await api.post('/api/payment/config', paymentConfig);
                            setPaymentConfigured(true);
                            showToast('支付配置已保存', 'success', 3000);
                          } catch (err) {
                            showToast('保存失败: ' + (err.message || '未知错误'), 'warning');
                          } finally {
                            setPaymentSaving(false);
                          }
                        }}
                        disabled={paymentSaving}
                        style={{
                          flex: 1, padding: '10px 0', background: 'linear-gradient(135deg, #eab308, #ca8a04)',
                          border: 'none', borderRadius: 8, cursor: paymentSaving ? 'wait' : 'pointer',
                          color: '#0f172a', fontSize: 13, fontWeight: 600, opacity: paymentSaving ? 0.6 : 1
                        }}
                      >
                        {paymentSaving ? '保存中...' : paymentConfigured ? '更新配置' : '保存配置'}
                      </button>
                    </div>

                    <div style={{ padding: 12, borderRadius: 8, background: 'rgba(234,179,8,0.05)', border: '1px solid rgba(234,179,8,0.15)' }}>
                      <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                        <b style={{ color: '#eab308' }}>配置说明：</b>
                      </p>
                      <ol style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                        <li>前往 <a href="https://www.xunhupay.com" target="_blank" rel="noreferrer" style={{ color: '#60a5fa' }}>虎皮椒官网</a> 注册并获取 APPID 和 APPSECRET</li>
                        <li>在虎皮椒后台设置回调地址为：<code style={{ color: '#facc15', background: 'rgba(0,0,0,0.3)', padding: '1px 4px', borderRadius: 3 }}>{window.location.origin}/api/payment/notify</code></li>
                        <li>兑换比例决定用户充值的性价比</li>
                        <li>充值套餐金额展示给用户选择，也可以允许自定义金额</li>
                      </ol>
                    </div>
                  </div>
                ) : activeSelection.type === 'sms' ? (
                  // 📱 短信验证配置面板
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    <div style={{ paddingBottom: 12, borderBottom: '1px solid var(--border-subtle)' }}>
                      <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--text-body)', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Smartphone size={18} style={{ color: '#10b981' }} />
                        短信验证配置
                      </h3>
                      <p style={{ margin: '4px 0 0 0', fontSize: 12, color: 'var(--text-muted)' }}>
                        配置阿里云短信服务，用于注册手机验证和找回密码
                      </p>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>AccessKey ID</label>
                      <input type="text" value={smsConfig.accessKeyId} onChange={e => setSmsConfig(prev => ({ ...prev, accessKeyId: e.target.value }))} placeholder="阿里云 AccessKey ID" style={{ width: '100%', padding: '8px 12px', background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)', borderRadius: 6, color: 'var(--text-body)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>AccessKey Secret</label>
                      <div style={{ position: 'relative' }}>
                        <input type={showSmsSecret ? 'text' : 'password'} value={smsConfig.accessKeySecret} onChange={e => setSmsConfig(prev => ({ ...prev, accessKeySecret: e.target.value }))} placeholder="阿里云 AccessKey Secret" style={{ width: '100%', padding: '8px 12px', paddingRight: 36, background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)', borderRadius: 6, color: 'var(--text-body)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                        <button onClick={() => setShowSmsSecret(!showSmsSecret)} style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', padding: 2 }}>
                          {showSmsSecret ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>短信签名</label>
                      <input type="text" value={smsConfig.signName} onChange={e => setSmsConfig(prev => ({ ...prev, signName: e.target.value }))} placeholder="留空默认：速通互联验证码" style={{ width: '100%', padding: '8px 12px', background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)', borderRadius: 6, color: 'var(--text-body)', fontSize: 13, outline: 'none', boxSizing: 'border-box' }} />
                      <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--text-muted)' }}>留空则使用赠送签名「速通互联验证码」，企业账号可填自定义签名</p>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>注册模板 Code</label>
                      <input type="text" value={smsConfig.templateCodeRegister} onChange={e => setSmsConfig(prev => ({ ...prev, templateCodeRegister: e.target.value }))} placeholder="留空默认：100001" style={{ width: '100%', padding: '8px 12px', background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)', borderRadius: 6, color: 'var(--text-body)', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'Monaco, monospace' }} />
                      <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--text-muted)' }}>用于注册/登录场景，留空默认赠送模板 100001</p>
                    </div>

                    <div>
                      <label style={{ display: 'block', marginBottom: 4, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>重置密码模板 Code</label>
                      <input type="text" value={smsConfig.templateCodeReset} onChange={e => setSmsConfig(prev => ({ ...prev, templateCodeReset: e.target.value }))} placeholder="留空默认：100003" style={{ width: '100%', padding: '8px 12px', background: 'var(--modal-input-bg)', border: '1px solid var(--border-subtle)', borderRadius: 6, color: 'var(--text-body)', fontSize: 13, outline: 'none', boxSizing: 'border-box', fontFamily: 'Monaco, monospace' }} />
                      <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--text-muted)' }}>用于找回密码场景，留空默认赠送模板 100003</p>
                    </div>

                    <div style={{ paddingTop: 12, borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 8 }}>
                      <button
                        onClick={async () => {
                          if (!smsConfig.accessKeyId || !smsConfig.accessKeySecret) {
                            showToast('请填写 AccessKey ID 和 Secret', 'warning', 4000);
                            return;
                          }
                          setSmsSaving(true);
                          try {
                            await api.post('/api/sms/config', smsConfig);
                            setSmsConfigured(true);
                            showToast('短信配置已保存', 'success', 3000);
                          } catch (err) {
                            showToast('保存失败: ' + (err.message || '未知错误'), 'warning');
                          } finally {
                            setSmsSaving(false);
                          }
                        }}
                        disabled={smsSaving}
                        style={{
                          flex: 1, padding: '10px 0', background: 'linear-gradient(135deg, #10b981, #059669)',
                          border: 'none', borderRadius: 8, cursor: smsSaving ? 'wait' : 'pointer',
                          color: '#fff', fontSize: 13, fontWeight: 600, opacity: smsSaving ? 0.6 : 1
                        }}
                      >
                        {smsSaving ? '保存中...' : smsConfigured ? '更新配置' : '保存配置'}
                      </button>
                    </div>

                    <div style={{ padding: 12, borderRadius: 8, background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.15)' }}>
                      <p style={{ margin: 0, fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                        <b style={{ color: '#10b981' }}>配置说明：</b>
                      </p>
                      <ol style={{ margin: '8px 0 0', paddingLeft: 20, fontSize: 11, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
                        <li>前往 <a href="https://ram.console.aliyun.com/manage/ak" target="_blank" rel="noreferrer" style={{ color: '#60a5fa' }}>阿里云 RAM 控制台</a> 获取 AccessKey</li>
                        <li>前往 <a href="https://dypns.console.aliyun.com" target="_blank" rel="noreferrer" style={{ color: '#60a5fa' }}>号码认证服务控制台</a> 开通短信核验功能</li>
                        <li>个人账号：签名和模板留空即可，自动使用赠送签名「速通互联验证码」和赠送模板</li>
                        <li>企业账号：可分别填写自定义签名和各场景模板Code</li>
                        <li>配置后用户注册时需通过手机短信验证，登录页会自动显示「找回密码」入口</li>
                        <li>短信验证码有效期 5 分钟，发送间隔 60 秒</li>
                      </ol>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'var(--text-muted)' }}>
                    <Settings size={48} color="#475569" style={{ marginBottom: 16 }} />
                    <p style={{ margin: 0 }}>← 请在左侧选择配置项</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {toastMessage && <AlertToast message={toastMessage} type={toastType} position="center" />}

      {confirmModal && (
        <ConfirmModal
          title={confirmModal.title}
          message={confirmModal.message}
          confirmText="确认"
          cancelText="取消"
          danger={confirmModal.danger}
          onConfirm={confirmModal.onConfirm}
          onCancel={confirmModal.onCancel}
        />
      )}
    </div>
  );

  return createPortal(modalContent, document.body);
}
